import {
  alpha,
  Box,
  Button,
  Dialog,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { navigate } from "gatsby";
import * as React from "react";
import { useEffect, useState } from "react";
import _ from "lodash";
import { useScreenSize } from "../../hooks/useScreenSize";
import { contentTexts } from "../../utils/contentTexts";
import { getOfferPath } from "../../utils/paths";
import {
  isPopupExcludedPath,
  POPUP_DELAY_MS,
  POPUP_REPEAT_AFTER_MS,
  POPUP_SCROLL_THRESHOLD,
  PROMO_ACTIVE,
  PROMO_POPUP_CTA_KEY,
  PROMO_POPUP_SEEN_KEY,
} from "./promoConfig";

const wasRecentlySeenOrConverted = () => {
  try {
    if (window.localStorage.getItem(PROMO_POPUP_CTA_KEY)) return true;
    const seenAt = Number(window.localStorage.getItem(PROMO_POPUP_SEEN_KEY));
    return Boolean(seenAt) && Date.now() - seenAt < POPUP_REPEAT_AFTER_MS;
  } catch {
    // Storage unavailable (private mode etc.) — better to stay quiet
    // than to nag on every page view.
    return true;
  }
};

const markSeen = () => {
  try {
    window.localStorage.setItem(PROMO_POPUP_SEEN_KEY, String(Date.now()));
  } catch {
    // ignore
  }
};

const markConverted = () => {
  try {
    window.localStorage.setItem(PROMO_POPUP_CTA_KEY, "1");
  } catch {
    // ignore
  }
};

type PromoPopupProps = {
  pathname: string;
};

const PromoPopupContent = ({
  onClose,
  onCta,
  isMobile,
}: {
  onClose: () => void;
  onCta: () => void;
  isMobile: boolean;
}) => {
  const { popup } = contentTexts.promo;

  return (
    <Stack
      spacing={isMobile ? 1.5 : 2}
      sx={{
        p: isMobile ? 3 : 4,
        pt: isMobile ? 2 : 3,
        position: "relative",
        color: (t) => t.palette.other.white,
      }}
    >
      <IconButton
        aria-label={popup.close}
        onClick={onClose}
        size="small"
        sx={{ position: "absolute", top: 8, right: 8, color: "inherit" }}
      >
        <IconX />
      </IconButton>
      <Typography
        component="span"
        sx={{
          color: "inherit",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          fontSize: isMobile ? 12 : 14,
          fontWeight: 700,
        }}
      >
        {popup.eyebrow}
      </Typography>
      <Typography variant="h3" component="h2" sx={{ color: "inherit" }}>
        {popup.titleLead}{" "}
        <Box
          component="span"
          sx={{
            display: "inline-block",
            px: 1.5,
            py: 0.25,
            border: "2px solid currentColor",
            borderRadius: 3,
            whiteSpace: "nowrap",
          }}
        >
          {popup.titleAmount}
        </Box>
      </Typography>
      <Typography variant={isMobile ? "body2" : "body1"} color="inherit">
        {popup.text}
      </Typography>
      <Button
        color="secondary"
        variant="invertedContained"
        fullWidth
        onClick={onCta}
      >
        {popup.cta}
      </Button>
      <Typography
        variant="caption"
        sx={{ color: (t) => alpha(t.palette.other.white, 0.75) }}
      >
        {popup.smallPrint}
      </Typography>
    </Stack>
  );
};

export const PromoPopup = ({ pathname }: PromoPopupProps) => {
  const [open, setOpen] = useState(false);
  const { isMobile } = useScreenSize();

  useEffect(() => {
    if (open || !PROMO_ACTIVE || isPopupExcludedPath(pathname)) return;
    if (wasRecentlySeenOrConverted()) return;

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      // The user may have navigated to an excluded page before the
      // timer fired — check again at trigger time.
      if (isPopupExcludedPath(window.location.pathname)) return;
      triggered = true;
      setOpen(true);
    };

    const timeout = setTimeout(trigger, POPUP_DELAY_MS);
    const handleScroll = _.throttle(() => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (
        scrollable > 0 &&
        window.scrollY / scrollable >= POPUP_SCROLL_THRESHOLD
      ) {
        trigger();
      }
    }, 200);
    window.addEventListener("scroll", handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pathname]);

  // Only an explicit action starts the 7-day pause: the X button (seen)
  // or the CTA (never again). Backdrop click / Esc just hide the popup,
  // so it may show again on a later visit.
  const handleDismiss = () => setOpen(false);

  const handleClose = () => {
    markSeen();
    setOpen(false);
  };

  const handleCta = () => {
    markConverted();
    setOpen(false);
    navigate(getOfferPath());
  };

  const content = (
    <PromoPopupContent
      onClose={handleClose}
      onCta={handleCta}
      isMobile={isMobile}
    />
  );

  // Bottom sheet on mobile (never a full-screen overlay), dialog on
  // desktop. Esc and backdrop click both fire onClose on either one.
  return isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleDismiss}
      PaperProps={{
        sx: {
          maxHeight: "50vh",
          m: 1.5,
          borderRadius: 4,
          backgroundColor: (t) => t.palette.primary.main,
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 4,
          borderRadius: 2,
          backgroundColor: (t) => alpha(t.palette.other.white, 0.5),
          mx: "auto",
          mt: 1.5,
        }}
      />
      {content}
    </Drawer>
  ) : (
    <Dialog
      open={open}
      onClose={handleDismiss}
      PaperProps={{
        sx: {
          maxWidth: 500,
          borderRadius: 3,
          backgroundColor: (t) => t.palette.primary.main,
        },
      }}
    >
      {content}
    </Dialog>
  );
};

export default PromoPopup;
